import { spacing } from '../spacing'
import { type ComponentCreation } from './types'
import type { Tokens } from '../augmentation'

/**
 * Modal is a lower-level construct that is leveraged by the following components:
 *
 * * [Dialog](https://mui.com/material-ui/api/dialog/)
 * * [Drawer](https://mui.com/material-ui/api/drawer/)
 * * [Menu](https://mui.com/material-ui/api/menu/)
 * * [Popover](https://mui.com/material-ui/api/popover/)
 */
export const createMuiModal = ({ variant }: Pick<Tokens, 'variant'>) =>
  ({
    MuiModal: {
      styleOverrides: {
        root: ({ theme: { breakpoints } }) => [
          {
            '&.DhlModal-container': {
              display: 'flex',
              padding: spacing(0, 3),
              backfaceVisibility: 'hidden',

              '& > .MuiPaper-root': {
                margin: 'auto',

                '&': variant !== 'mobile' && {
                  maxHeight: '80vh',
                },

                // Do not show an outline on the paper element itself when focused,
                // e.g., autoFocus with a focus trap
                '&.DhlModal-paper:focus, &.DhlModal-paper:focus-visible': {
                  outline: 'none',
                },

                '& .DhlModal-base': {
                  // Style any parent (e.g., form) that is wrapped around DhlModal-* elements
                  '& > :only-child:not(.DhlModal-content)': {
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    height: 0,
                  },
                },
              },

              '&.DhlModal-fullscreen': {
                padding: 0,

                '& > .MuiPaper-root': {
                  borderRadius: 0,
                  width: '100%',
                  height: '100%',
                  maxHeight: '100vh',
                },
              },
              ...(!['xl', 'lg', 'md'].includes(variant)
                ? {
                    '&:not(.DhlModal-fullscreen) > .DhlModal-paper': {
                      [breakpoints.up('xxl')]: {
                        width: '30%',
                      },
                      [breakpoints.between('xl', 'xxl')]: {
                        width: '40%',
                      },
                      [breakpoints.between('lg', 'xl')]: {
                        width: '60%',
                      },
                      [breakpoints.between('md', 'lg')]: {
                        width: '81%',
                      },
                      [breakpoints.between('sm', 'md')]: {
                        width: '87%',
                      },
                      [breakpoints.down('sm')]: {
                        width: '100%',
                      },
                    },
                  }
                : {
                    '&:not(.DhlModal-fullscreen) > .DhlModal-paper': {
                      width: '95vw',
                    },
                  }),
            },
          },
        ],
      },
    },
  }) satisfies ComponentCreation
